"use client";

import { useCallback, useState } from "react";
import MarkdownRenderer from "./MarkdownRenderer";
import { deepResearchCopyText } from "@/app/lib/deepResearchCopy";
import { deepResearchSpokenText } from "@/app/lib/deepResearchSpeech";
import type { DeepResearchSource } from "@/app/lib/types";

export interface DeepResearchHeaderMetadata {
  model?: string;
  durationSeconds?: number;
  costUsd?: number;
  corpusDocumentsRead?: number;
  corpusCitedCount?: number;
  webCitedCount?: number;
  sourceCount?: number;
  fromFallback?: boolean;
}

export function deepResearchHeaderLine(metadata: DeepResearchHeaderMetadata): string {
  const duration = metadata.durationSeconds === undefined ? undefined : formatDuration(metadata.durationSeconds);
  return [
    metadata.fromFallback ? "Brief fallback" : undefined,
    metadata.model === undefined ? undefined : displayModel(metadata.model),
    duration,
    metadata.costUsd === undefined ? undefined : `$${metadata.costUsd.toFixed(2)}`,
    metadata.corpusDocumentsRead === undefined ? undefined : `${metadata.corpusDocumentsRead} corpus reads, ${metadata.corpusCitedCount ?? 0} cited`,
    metadata.webCitedCount === undefined ? undefined : `${metadata.webCitedCount} web cited`,
  ].filter((segment): segment is string => segment !== undefined).join(" • ");
}

function displayModel(model: string): string {
  return model === "claude-fable-5" ? "Claude Fable 5" : model;
}

function formatDuration(seconds: number): string {
  const milliseconds = seconds * 1_000;
  if (milliseconds < 1_000) return `${Math.round(milliseconds)}ms`;
  const totalSeconds = Math.floor(milliseconds / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  return minutes > 0 ? `${minutes}m ${totalSeconds % 60}s` : `${totalSeconds}s`;
}

// INVARIANT: a corpus source must NEVER be handed to the browser at its
// /corpus/documents/{key} url -- that endpoint needs an authenticated licensed
// seat. The in-app reader is the default; the Studio link is the only way out.
//
// Owner decision 2026-10-06 (#433): all corpus sources with a studio_url open
// on the Studio; the Studio now formats text documents (datalake #866).
function studioUrlForCorpusSource(source: DeepResearchSource): string | undefined {
  return source.studio_open_url ?? source.studio_url;
}

interface DeepResearchCardProps {
  report?: string;
  sources?: DeepResearchSource[];
  brief: string;
  fromFallback?: boolean;
  terminalMessage?: string;
  status?: "cancelled" | "failed" | "error";
  metadata?: DeepResearchHeaderMetadata;
  onOpenCorpusDocument?: (source: DeepResearchSource) => void;
  recordId?: string;
  onReadAloud?: (recordId: string, providerId: string, text: string) => void;
  ttsProviderId?: string;
  isTTSActive?: boolean;
  ttsProgress?: number;
  isTTSCached?: boolean;
}

export default function DeepResearchCard({ report, sources = [], brief, fromFallback, terminalMessage, status, metadata, onOpenCorpusDocument, recordId, onReadAloud, ttsProviderId, isTTSActive, ttsProgress, isTTSCached }: DeepResearchCardProps) {
  const [copied, setCopied] = useState(false);
  const stopped = status === "cancelled";
  const corpusSources = sources.filter((source) => source.origin === "corpus");
  const webSources = sources.filter((source) => source.origin !== "corpus");
  const headerLine = deepResearchHeaderLine({
    ...metadata,
    fromFallback: fromFallback ?? metadata?.fromFallback,
    corpusCitedCount: metadata?.corpusCitedCount,
    webCitedCount: metadata?.sourceCount === undefined ? undefined : webSources.length,
  });
  const corpusSourceForHref = (href: string) => {
    const pathname = new URL(href, window.location.href).pathname;
    return corpusSources.find((source) => new URL(source.url, window.location.href).pathname === pathname);
  };
  const openCorpusSource = (source: DeepResearchSource) => {
    const studioUrl = studioUrlForCorpusSource(source);
    if (studioUrl) {
      window.open(studioUrl, "_blank", "noopener,noreferrer");
      return;
    }
    onOpenCorpusDocument?.(source);
  };
  const handleCorpusLink = (event: React.MouseEvent<HTMLElement>) => {
    const anchor = (event.target as HTMLElement).closest("a");
    const href = anchor?.getAttribute("href");
    if (!href) return;
    const source = corpusSourceForHref(href);
    if (!source) return;
    event.preventDefault();
    openCorpusSource(source);
  };
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(deepResearchCopyText({ headerLine, report, sources })).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1_500);
    });
  }, [headerLine, report, sources]);
  const handleReadAloud = useCallback(() => {
    if (!onReadAloud || !recordId || !ttsProviderId || !report) return;
    onReadAloud(recordId, ttsProviderId, deepResearchSpokenText(report));
  }, [onReadAloud, recordId, report, ttsProviderId]);
  const showReadAloud = !!report && !!onReadAloud && !!recordId && !!ttsProviderId;
  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", flexShrink: 0, padding: "16px", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", background: "var(--surface)" }}>
      <header style={{ position: "relative", height: "64px", flexShrink: 0, padding: "8px 12px", margin: "-16px -16px 12px", background: "color-mix(in srgb, #cc6144 6%, transparent)", borderBottom: "1px solid var(--border)", boxSizing: "border-box" }}>
        <div style={{ color: "#cc6144", fontSize: "12px", fontWeight: 600 }}>Deep Research</div>
        {headerLine && <div title={headerLine} style={{ color: "var(--text-secondary)", fontFamily: "var(--font-geist-mono), monospace", fontSize: "10px", lineHeight: "14px", display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden", textOverflow: "ellipsis" }}>{headerLine}</div>}
        {report && <button onClick={handleCopy} className="btn-ghost" style={{ position: "absolute", top: 4, right: 7, padding: "2px", lineHeight: 0, opacity: 0.75 }} title="Copy report" aria-label="Copy report">
          {copied ? (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13.25 4.75l-6.5 6.5-3.5-3.5" /></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5" /><path d="M10.5 5.5V3.5a1.5 1.5 0 00-1.5-1.5H3.5A1.5 1.5 0 002 3.5V9a1.5 1.5 0 001.5 1.5h2" /></svg>
          )}
        </button>}
        {showReadAloud && <button onClick={handleReadAloud} className="btn-ghost" style={{ position: "absolute", top: 4, right: 29, padding: "2px", lineHeight: 0, opacity: isTTSActive ? 1 : 0.5, color: isTTSActive ? "#cc6144" : undefined }} title={isTTSActive ? "Playing" : isTTSCached ? "Play (cached)" : "Play (will synthesize)"} aria-label={isTTSActive ? "Currently playing" : "Read this report aloud"}>
          {isTTSActive ? (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="6" width="2" height="4" rx="0.5" /><rect x="7" y="3" width="2" height="10" rx="0.5" /><rect x="11" y="5" width="2" height="6" rx="0.5" /></svg>
          ) : isTTSCached ? (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M5 3.5l7 4.5-7 4.5V3.5z" /></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3.5l7 4.5-7 4.5V3.5z" /></svg>
          )}
        </button>}
      </header>
      <div style={{ height: "calc(100dvh - var(--bottom-bar-h, 120px) - var(--attachment-bar-h, 0px) - 160px)", minHeight: 0, overflowY: "auto" }}>
        {report ? <>
          <div onClickCapture={handleCorpusLink}><MarkdownRenderer content={report} isStreamComplete /></div>
        </> : <p style={{ margin: 0, color: stopped ? "var(--text-secondary)" : "var(--error)" }}>{stopped ? `Research stopped: ${terminalMessage || "Stopped by you."}` : terminalMessage || "Research did not produce a report."}</p>}
        {sources.length > 0 && <div style={{ marginTop: "16px" }}><strong>Sources</strong>
          {corpusSources.length > 0 && <section><h3 style={{ fontSize: "14px", margin: "10px 0 6px", color: "#cc6144" }}>Library</h3><ol style={{ listStyleType: "none", margin: 0, paddingLeft: "20px" }}>{corpusSources.map((source, index) => { const studioUrl = studioUrlForCorpusSource(source); return <li key={`${source.url}-${index}`} style={{ color: "#cc6144" }}><a href={studioUrl || source.url} {...(studioUrl ? { target: "_blank", rel: "noopener noreferrer" } : {})} onClick={(event) => { if (studioUrl) return; event.preventDefault(); onOpenCorpusDocument?.(source); }}>{source.label ?? "C" + (index + 1)}. {source.title || source.url}{source.publication_label && <span style={{ color: "var(--text-secondary)" }}> — {source.publication_label}</span>}</a></li>; })}</ol></section>}
          {webSources.length > 0 && <section><h3 style={{ fontSize: "14px", margin: "10px 0 6px" }}>Web</h3><ol style={{ listStyleType: "none", margin: 0, paddingLeft: "20px" }}>{webSources.map((source, index) => <li key={`${source.url}-${index}`}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.label ?? index + 1}. {source.title || source.url}{source.publication_label && <span style={{ color: "var(--text-secondary)" }}> — {source.publication_label}</span>}</a></li>)}</ol></section>}
        </div>}
        <details style={{ marginTop: "16px" }}><summary>{fromFallback ? "Brief could not be drafted; the question was researched as written." : "Research brief"}</summary><div style={{ marginTop: "8px", whiteSpace: "pre-wrap", color: "var(--text-secondary)" }}>{brief}</div></details>
      </div>
      {isTTSActive && typeof ttsProgress === "number" && <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, background: "color-mix(in srgb, #cc6144 20%, transparent)", pointerEvents: "none", borderRadius: "0 0 var(--radius-lg) var(--radius-lg)", overflow: "hidden" }}><div style={{ height: "100%", width: `${Math.min(100, Math.max(0, ttsProgress * 100))}%`, background: "#cc6144", transition: "width 120ms linear" }} /></div>}
    </div>
  );
}
